import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Form, Tabs, Empty, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import './DiscountSection.scss';
import CouponAndVoucherCell from 'pages/wallet/couponsAndVouchers/subviews/CouponAndVoucherCell';
import couponRoundIcon from 'assets/images/coupon_round.png';
import { BackendLoyaltyItem } from 'types/dto/wallet';
import { fetchLoyaltyItems } from 'services/walletService';

interface DiscountSectionProps {
  isReadonly?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onLoadError?: (error: boolean) => void;
}

const DiscountSection: React.FC<DiscountSectionProps> = ({
  isReadonly = false,
  onLoadingChange,
  onLoadError,
}) => {
  const { t } = useTranslation('new_order');
  const { t: tWallet } = useTranslation('wallet');
  const { i18n } = useTranslation();

  const [activeCoupons, setActiveCoupons] = useState<BackendLoyaltyItem[]>([]);
  const [activeVouchers, setActiveVouchers] = useState<BackendLoyaltyItem[]>(
    []
  );
  const [couponsTotal, setCouponsTotal] = useState(0);
  const [vouchersTotal, setVouchersTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const groupAndCountLoyaltyItems = (items: BackendLoyaltyItem[]) => {
    // Group vouchers by their token and count occurrences
    const groupedVouchers = items.reduce(
      (
        acc: Record<string, { item: BackendLoyaltyItem; count: number }>,
        voucher: BackendLoyaltyItem
      ) => {
        const token = voucher.code;

        const remainingCount =
              voucher.usage?.total !== undefined &&
              voucher.usage?.used !== undefined
                ? Math.max(1, voucher.usage?.total - voucher.usage?.used)
                : 1;

        if (!acc[token]) {
          acc[token] = {
            item: voucher,
            count: remainingCount,
          };
        }else{
          acc[token].count += remainingCount;
        }
        return acc;
      },
      {}
    );

    // console.log('groupedVouchers', groupedVouchers);


    // Create new array with updated usage information
    const uniqueVouchers = Object.values(groupedVouchers).map(
      ({ item, count }) => ({
        ...item,
        usage: {
          total: count,
          used: 0,
          percentage: 0,
        },
      })
    );

    return uniqueVouchers;
  };

  // Fetch active coupons and vouchers from backend
  useEffect(() => {
    const loadActiveItems = async () => {
      try {
        if (page === 1) {
          onLoadingChange?.(true);
        } else {
          setIsFetchingMore(true);
        }
        onLoadError?.(false);

        // Call API to fetch data
        const [couponsRes, vouchersRes] = await Promise.all([
                    fetchLoyaltyItems('PRO-', '', page),
                    fetchLoyaltyItems('VOC-', '', page)
                  ]);

        // Get the actual loyalty items list
        setCouponsTotal(couponsRes?.data.total);
        setVouchersTotal(vouchersRes?.data.total);

        const coupons = couponsRes?.data.results;
        const vouchers = vouchersRes?.data.results;

        setActiveCoupons(prev => {
          const processed = groupAndCountLoyaltyItems(coupons);
          return page === 1 ? processed : [...prev, ...processed];
        });

        setActiveVouchers(prev => {
          const processed = groupAndCountLoyaltyItems(vouchers);
          return page === 1 ? processed : [...prev, ...processed];
        });
      } catch (err) {
        console.error('Failed to fetch loyalty items:', err);
        onLoadError?.(true);
      } finally {
        onLoadingChange?.(false);
        setIsFetchingMore(false);
      }
    };

    loadActiveItems();
  }, [i18n.language, tWallet, onLoadingChange, onLoadError, page]);

  const form = Form.useFormInstance();
  const promotionCode = Form.useWatch('promotionCode', form) || '';

  const selectedIds: number[] = useMemo(() => {
    if (!promotionCode) return [];
    return promotionCode
      .split(',')
      .map((code: string) => code.trim())
      .filter((code: string) => code !== '')
      .map((code: string) => Number(code))
      .filter((id: number) => !isNaN(id));
  }, [promotionCode]);

  const allSelectedDiscounts = useMemo(() => {
    // Filter selected coupons and vouchers
    const selectedCoupons = activeCoupons.filter((coupon) =>
      selectedIds.includes(coupon.id)
    );
    const selectedVouchers = activeVouchers.filter((voucher) =>
      selectedIds.includes(voucher.id)
    );

    return [...selectedCoupons, ...selectedVouchers];
  }, [selectedIds, activeCoupons, activeVouchers]);

  const handleDiscountSelection = useCallback(
    async (discountId: number, selected: boolean) => {
      let newSelectedIds: number[];

      const isCoupon = activeCoupons.some((coupon) => coupon.id === discountId);
      if (selected) {
        if (isCoupon) {
          const voucherIds = selectedIds.filter((id) =>
            activeVouchers.some((v) => v.id === id)
          );
          newSelectedIds = [discountId, ...voucherIds];
        } else {
          newSelectedIds = [...selectedIds, discountId];
        }
      } else {
        newSelectedIds = selectedIds.filter((id: number) => id !== discountId);
      }

      const allData = [...activeCoupons, ...activeVouchers];
      const newSelectedTokens = allData
        .filter((item) => newSelectedIds.includes(item.id))
        .map((item) => item.code);

      form.setFieldsValue({
        promotionCode: newSelectedIds.join(','),
        loyaltyTokens: newSelectedTokens,
      });

      // console.log('Updated tokens in form:', newSelectedTokens);
    },
    [form, selectedIds, activeCoupons, activeVouchers]
  );

  const renderCouponList = (
    coupons: BackendLoyaltyItem[],
    type: 'coupons' | 'vouchers'
  ) => {
    if (coupons.length === 0) {
      return (
        <Empty
          image={<img src={couponRoundIcon} className="empty-state-image" />}
          description={
            type === 'coupons'
              ? tWallet('noCouponsAvailable')
              : tWallet('noVouchersAvailable')
          }
        />
      );
    }

    const handleNextPage = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setPage(prev => prev + 1);
    };

    const total = type === 'coupons' ? couponsTotal : vouchersTotal;
    const PAGE_SIZE = 10;
    const hasMore = (page * PAGE_SIZE) < total;

    return (
      <Form.Item name="promotionCode">
        <div className="coupon-grid">
          {coupons.map((coupon) => (
            <CouponAndVoucherCell
              key={coupon.id}
              coupon={coupon}
              isExpiredOrUsed={false}
              isSelected={selectedIds.includes(coupon.id)}
              onSelectionChange={handleDiscountSelection}
              needCheckBox={true}
            />
          ))}
        </div>
        {hasMore && (
          <div className="more-discounts-footer">
            <Button
              type="link"
              htmlType="button"
              loading={isFetchingMore}
              onClick={handleNextPage}
            >
              {tWallet('more')}
            </Button>
          </div>
        )}
      </Form.Item>
    );
  };

  const tabItems = [
    {
      key: 'vouchers',
      label: `${tWallet('vouchers')} (${vouchersTotal})`,
      children: renderCouponList(activeVouchers, 'vouchers'),
    },
    {
      key: 'coupons',
      label: `${tWallet('coupons')} (${couponsTotal})`,
      children: renderCouponList(activeCoupons, 'coupons'),
    },
  ];

  return (
    <div className="discount-section">
      <Form.Item name="loyaltyTokens" noStyle>
        <input type="hidden" />
      </Form.Item>
      <span className="vls-section-title">{t('paymentDiscounts')}</span>
      <div className="discount-disclaimer">{t('discountDisclaimer')}</div>

      {isReadonly ? (
        allSelectedDiscounts.length > 0 ? (
          <Form.Item name="promotionCode">
            <div className="selected-discounts-grid">
              {allSelectedDiscounts.map((discount) => (
                <CouponAndVoucherCell
                  key={discount.id}
                  coupon={discount}
                  isExpiredOrUsed={false}
                  needCheckBox={false}
                />
              ))}
            </div>
          </Form.Item>
        ) : (
          <div className="no-discounts-message">{t('noDiscountsSelected')}</div>
        )
      ) : (
        <Tabs
          defaultActiveKey="vouchers"
          items={tabItems}
          className="coupons-tabs"
          size="large"
        />
      )}
    </div>
  );
};

export default DiscountSection;
